import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useBranchStore } from '../../store';
import { exportRowsToExcel, fetchAllRows } from '../../utils/excel';
import DateTimeRange, { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import './AuthLogs.css';

const PMS_LABELS = {
    capitaland: 'ODX',
    odx:        'ODX',
    winhms:     'WinHMS',
    wincloud:   'WinCloud',
    idsnext:    'IDS Next',
    idx:        'IDS Next',
    hotla:      'HOTLA',
    hotelkey:   'HotelKey',
    innkey:     'InnKey',
};
const PMS_FILTER = ['capitaland', 'winhms', 'wincloud', 'idsnext', 'hotla', 'hotelkey', 'innkey'];

// How the guest signed in — the portal's kind of sign-in (guest.userType).
const METHOD_LABELS = {
    guest:    'Room (PMS)',
    otp:      'OTP (WhatsApp / SMS)',
    visitor:  'Voucher',
    isp:      'ISP plan',
    resident: 'Resident',
    staff:    'Staff',
};
// A room sign-in names its hotel system; the rest, the way in.
const methodLabel = ({ provider, userType }) => (provider
    ? `${PMS_LABELS[provider] || provider} · PMS`
    : (METHOD_LABELS[userType] || 'Other'));

const RESULT_LABELS = { SUCCESS: 'Signed in', FAILED: 'Refused', ERROR: 'Error', TIMEOUT: 'Timeout', PENDING: 'Not finished', CANCELLED: 'Cancelled' };
// The result filter — the same choices as the figures (an error or a timeout, one choice).
const RESULT_FILTER = [
    ['SUCCESS', 'Signed in'], ['FAILED', 'Refused'], ['ERROR,TIMEOUT', 'Error / timeout'], ['PENDING', 'Not finished'], ['CANCELLED', 'Cancelled'],
];

const EMPTY_FILTERS = { provider: '', userType: '', status: '', room: '', mac: '', correlationId: '', from: '', to: '' };

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
}

function fmtMs(ms) {
    if (!ms) return '—';
    return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(2)}s`;
}

// A room sign-in: how long its hotel system took to answer — or that it did not.
function pmsNote(row) {
    const took = row.pms?.durationMs ? fmtMs(row.pms.durationMs) : '';
    const checked = row.result?.verificationStatus;
    if (checked === 'TIMEOUT') return `Hotel system: no answer${took ? ` in ${took}` : ''}`;
    if (checked === 'ERROR') return 'Hotel system: error';
    return took ? `Hotel system answered in ${took}` : '';
}

// The Excel file: the table's columns, and what the table leaves to the View page.
const EXPORT_COLUMNS = [
    { label: 'Time', field: 'createdAt' },
    { label: 'Session ID', field: 'authSessionId' },
    { label: 'Correlation ID', field: 'correlationId' },
    { label: 'Method', value: (r) => methodLabel({ provider: r.pms?.provider, userType: r.guest?.userType }) },
    { label: 'Property', field: 'branchName' },
    { label: 'Room', value: (r) => r.guest?.roomNumber },
    { label: 'Guest', value: (r) => r.guest?.guestName || r.guest?.surname || r.guest?.email },
    { label: 'Mobile', value: (r) => r.guest?.mobile },
    { label: 'Booking', value: (r) => r.guest?.bookingId },
    { label: 'MAC', value: (r) => r.network?.mac },
    { label: 'IP', value: (r) => r.network?.ip },
    { label: 'VLAN', value: (r) => r.network?.vlan },
    { label: 'Device', value: (r) => [r.network?.deviceType, r.network?.os].filter(Boolean).join(' · ') },
    { label: 'NAS', value: (r) => r.nas?.nasIdentifier || r.nas?.nasIp },
    { label: 'PMS Duration (ms)', value: (r) => r.pms?.durationMs },
    { label: 'PMS Result', value: (r) => r.result?.verificationStatus },
    { label: 'Status', value: (r) => r.result?.authorizationStatus },
    { label: 'Error', value: (r) => [r.result?.errorCode, r.result?.errorMessage].filter(Boolean).join(' — ') },
];

export default function AuthLogs() {
    const navigate = useNavigate();
    // The branch (and, for a superadmin, the tenant) picked in the header: its logs only.
    const branchId = useBranchStore((s) => s.selectedBranch?._id);
    const tenantId = useBranchStore((s) => s.selectedTenant?._id);

    const [rows,    setRows]    = useState([]);
    const [stats,   setStats]   = useState(null);
    const [loading, setLoading] = useState(false);
    const [total,   setTotal]   = useState(0);
    const [page,    setPage]    = useState(1);

    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const limit = 50;

    // The figures of the same branch and the same dates as the list below.
    const fetchStats = useCallback(async () => {
        try {
            const params = { branchId, tenantId, ...rangeParams({ from: filters.from, to: filters.to }) };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await api.get('/auth-logs/stats', { params });
            setStats(r.data.data);
        } catch (_) {}
    }, [branchId, tenantId, filters.from, filters.to]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            // The date and time as ISO — the server has no idea of India time.
            const params = { page, limit, ...filters, ...rangeParams(filters), branchId, tenantId };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const r = await api.get('/auth-logs', { params });
            setRows(r.data.data || []);
            setTotal(r.data.total || 0);
        } catch (_) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters, branchId, tenantId]);

    // Another branch picked: back to its first page.
    useEffect(() => { setPage(1); }, [branchId, tenantId]);
    useEffect(() => { fetchStats(); }, [fetchStats]);
    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    function handleFilter(e) {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    }

    // Every log the filters match (not just this page), up to 10,000, as an Excel file.
    const [exporting, setExporting] = useState(false);
    async function handleExport() {
        setExporting(true);
        try {
            const params = { ...filters, ...rangeParams(filters), branchId, tenantId };
            Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
            const { rows: all, capped } = await fetchAllRows(async ({ page: p, limit: l }) => {
                const r = await api.get('/auth-logs', { params: { ...params, page: p, limit: l } });
                return { rows: r.data.data || [], total: r.data.total };
            }, { pageSize: 1000 });
            exportRowsToExcel(`authorization-logs-${new Date().toISOString().slice(0, 10)}`, EXPORT_COLUMNS, all, { sheetName: 'Authorization Logs' });
            if (capped) toast.info('The first 10,000 logs were exported — narrow the dates or filters for the rest.');
        } catch (_) {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    }

    const pages = Math.ceil(total / limit);

    return (
        <div className="al-wrap">
            <div className="al-header">
                <h1 className="al-title">Authorization Logs</h1>
                <div className="al-header-actions">
                    <button className="al-refresh" onClick={handleExport} disabled={exporting || total === 0}>
                        {exporting ? 'Exporting…' : '⬇ Export Excel'}
                    </button>
                    <button className="al-refresh" onClick={() => { fetchStats(); fetchLogs(); }}>↻ Refresh</button>
                </div>
            </div>

            {/* The figures — each one shows its own sign-ins in the table below */}
            {stats && <Summary totals={stats.totals || {}} status={filters.status} onPick={(status) => { setFilters(f => ({ ...f, status })); setPage(1); }} />}

            {/* How the guests signed in */}
            {stats?.methods?.length > 0 && (
                <div className="al-methods">
                    <span className="al-methods-title">How guests signed in</span>
                    {mergedMethods(stats.methods).map(m => {
                        const active = m.provider ? filters.provider === m.provider : (m.userType && filters.userType === m.userType && !filters.provider);
                        return (
                            <button key={m.label} type="button" className={`al-method${active ? ' active' : ''}`}
                                title="Show these sign-ins"
                                onClick={() => { setFilters(f => ({ ...f, provider: m.provider || '', userType: m.provider ? '' : (m.userType || '') })); setPage(1); }}>
                                <span className="al-method-name">{m.label}</span>
                                <span className="al-method-total">{m.total}</span>
                                <span className="al-method-ok"><i className="fas fa-check" aria-hidden="true"></i> {m.success}</span>
                                {m.failed > 0 && <span className="al-method-fail"><i className="fas fa-times" aria-hidden="true"></i> {m.failed}</span>}
                                {m.avgDuration != null && <span className="al-method-avg" title="How long the hotel system took to answer, on average">{fmtMs(m.avgDuration)}</span>}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Filters */}
            <div className="al-filters">
                <select name="userType" value={filters.userType} onChange={handleFilter} aria-label="Sign-in method">
                    <option value="">All methods</option>
                    {Object.entries(METHOD_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
                <select name="provider" value={filters.provider} onChange={handleFilter} aria-label="Hotel system">
                    <option value="">All hotel systems</option>
                    {PMS_FILTER.map((p) => <option key={p} value={p}>{PMS_LABELS[p]}</option>)}
                </select>
                <select name="status" value={filters.status} onChange={handleFilter} aria-label="Result">
                    <option value="">All results</option>
                    {RESULT_FILTER.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select>
                <input name="room"          placeholder="Room #"         value={filters.room}          onChange={handleFilter} />
                <input name="mac"           placeholder="MAC address"    value={filters.mac}           onChange={handleFilter} />
                <input name="correlationId" placeholder="Correlation ID" value={filters.correlationId} onChange={handleFilter} />
                <DateTimeRange value={filters} onChange={({ from, to }) => { setFilters(f => ({ ...f, from, to })); setPage(1); }} />
                <button className="al-clear" onClick={() => { setFilters(EMPTY_FILTERS); setPage(1); }}>Clear</button>
            </div>

            {/* Table */}
            <div className="al-table-wrap">
                <table className="al-table">
                    <thead>
                        <tr>
                            <th className="al-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>Method</th>
                            <th>Guest</th>
                            <th>MAC</th>
                            <th>Property · NAS</th>
                            <th>Result</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={8} className="al-loading">Loading…</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={8} className="al-empty">No records found</td></tr>
                        )}
                        {!loading && rows.map((row, i) => {
                            const status = row.result?.authorizationStatus || 'PENDING';
                            const guestName = row.guest?.guestName || row.guest?.surname || row.guest?.email;
                            const refusedWhy = ['FAILED', 'ERROR', 'TIMEOUT'].includes(status) && row.result?.errorMessage;
                            return (
                            <tr key={row._id} className="al-row">
                                <td className="al-sr">{(page - 1) * limit + i + 1}</td>
                                <td className="al-time">
                                    {fmt(row.createdAt)}
                                    <div className="al-note al-mono">{row.authSessionId}</div>
                                </td>
                                <td>
                                    <span className="al-method-badge">{methodLabel({ provider: row.pms?.provider, userType: row.guest?.userType })}</span>
                                    {row.pms?.provider && pmsNote(row) && <div className="al-note">{pmsNote(row)}</div>}
                                </td>
                                <td>
                                    {guestName || (row.guest?.mobile && /^\+?\d{6,}$/.test(row.guest.mobile) ? row.guest.mobile : '—')}
                                    {(row.guest?.roomNumber || (guestName && row.guest?.mobile && /^\+?\d{6,}$/.test(row.guest.mobile))) && (
                                        <div className="al-note">
                                            {[row.guest?.roomNumber && `Room ${row.guest.roomNumber}`, guestName && /^\+?\d{6,}$/.test(row.guest?.mobile || '') && row.guest.mobile].filter(Boolean).join(' · ')}
                                        </div>
                                    )}
                                </td>
                                <td className="al-mono al-small">{row.network?.mac || '—'}</td>
                                <td>
                                    {row.branchName || '—'}
                                    {(row.nas?.nasIdentifier || row.nas?.nasIp) && <div className="al-note al-mono">{row.nas.nasIdentifier || row.nas.nasIp}</div>}
                                </td>
                                <td>
                                    <StatusBadge s={status} />
                                    {refusedWhy && <div className="al-note al-why">{refusedWhy}</div>}
                                </td>
                                <td>
                                    <button className="al-view-btn" onClick={() => navigate(`/auth-logs/${row._id}`)}>
                                        View
                                    </button>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="al-pagination">
                <span className="al-total">Total: {total}</span>
                <button disabled={page <= 1}    onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                <span>Page {page} / {pages || 1}</span>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
        </div>
    );
}

/**
 * The figures in one strip: every sign-in attempt, how many signed in (and the
 * share), refused, errors and timeouts, not finished — and how long the hotel
 * system took, when rooms signed in. A figure clicked shows its sign-ins.
 */
function Summary({ totals, status, onPick }) {
    const total = totals.total || 0;
    const share = (n) => (total ? `${Math.round((n / total) * 100)}%` : '');
    const items = [
        { key: '', label: 'Sign-in attempts', value: total },
        { key: 'SUCCESS', label: 'Signed in', value: totals.success || 0, note: share(totals.success || 0), tone: 'ok' },
        { key: 'FAILED', label: 'Refused', value: totals.failed || 0, note: share(totals.failed || 0), tone: 'bad' },
        {
            key: 'ERROR,TIMEOUT', label: 'Errors', value: (totals.errors || 0) + (totals.timeouts || 0), tone: 'warn',
            note: totals.timeouts ? `${totals.timeouts} timed out` : '',
        },
        { key: 'PENDING', label: 'Not finished', value: totals.pending || 0, title: 'Waiting for an admin\'s approval, or left before the end' },
    ];
    // Colour only for an outcome that happened — a zero stays plain.
    const toneOf = (item) => (item.tone && item.value > 0 ? ` al-sum-${item.tone}` : '');
    return (
        <div className="al-summary" role="group" aria-label="Figures">
            {items.map((item) => (
                <button key={item.label} type="button" title={item.title || `Show: ${item.label}`}
                    className={`al-sum${toneOf(item)}${status === item.key ? ' active' : ''}`}
                    onClick={() => onPick(item.key)}>
                    <span className="al-sum-label">{item.label}</span>
                    <span className="al-sum-value">{item.value.toLocaleString('en-IN')}</span>
                    {item.note ? <span className="al-sum-note">{item.note}</span> : null}
                </button>
            ))}
            {totals.avgDuration != null && (
                <div className="al-sum al-sum-static" title="How long the hotel system (PMS) took to answer a room sign-in, on average">
                    <span className="al-sum-label">Hotel system reply</span>
                    <span className="al-sum-value">{fmtMs(totals.avgDuration)}</span>
                    <span className="al-sum-note">average</span>
                </div>
            )}
        </div>
    );
}

// The per-method figures with one label each (a room sign-in with no hotel system
// recorded and the plain "Room (PMS)" are one line), the busiest first.
function mergedMethods(methods) {
    const byLabel = new Map();
    for (const m of methods) {
        const label = methodLabel(m);
        const line = byLabel.get(label) || { label, provider: m.provider, userType: m.userType, total: 0, success: 0, failed: 0, avgDuration: null, timed: 0 };
        line.total += m.total || 0;
        line.success += m.success || 0;
        line.failed += m.failed || 0;
        if (m.avgDuration != null) {
            line.avgDuration = ((line.avgDuration || 0) * line.timed + m.avgDuration * (m.total || 1)) / (line.timed + (m.total || 1));
            line.timed += m.total || 1;
        }
        byLabel.set(label, line);
    }
    return [...byLabel.values()].sort((a, b) => b.total - a.total);
}

function StatusBadge({ s }) {
    if (!s) return <span className="al-badge al-badge-pending">—</span>;
    return <span className={`al-badge al-badge-${s.toLowerCase()}`}>{RESULT_LABELS[s] || s}</span>;
}
